import { z } from "zod";
import {
  wordSchema,
  exampleSchema,
  cardSchema,
  stateSchema,
  eventSchema,
  sessionSchema,
  deckSchema,
  settingsSchema,
  defaults,
  validExample,
  parseMarked,
  markExample,
  examplesFor,
  wordKey,
  id,
  type Snapshot,
  type WordSense,
  type ExampleSentence,
} from "./domain";
import { db, tables, SentenceDB, snapshot } from "./db";
import { newState } from "./scheduler";
const limited = <T extends z.ZodType>(s: T) => z.array(s).max(100000);
const backupSchema = z.object({
  app: z.literal("sentence"),
  schemaVersion: z.literal(2),
  exportedAt: z.iso.datetime(),
  words: limited(wordSchema),
  examples: limited(exampleSchema),
  cards: limited(cardSchema),
  states: limited(stateSchema),
  events: limited(eventSchema),
  sessions: limited(sessionSchema),
  decks: limited(deckSchema),
  settings: settingsSchema,
});
export type Backup = z.infer<typeof backupSchema>;
export function migrateBackup(value: unknown): unknown {
  if (!value || typeof value !== "object") return value;
  const v = value as Record<string, unknown>;
  if (v.app === "sentence" && v.schemaVersion === 1)
    return {
      ...v,
      schemaVersion: 2,
      settings: { ...defaults, ...(v.settings as object) },
      events: Array.isArray(v.events)
        ? v.events.map((e) => ({ ...e, kind: e.kind ?? "review" }))
        : v.events,
    };
  return value;
}
export function validateBackup(raw: unknown): Backup {
  const result = backupSchema.safeParse(migrateBackup(raw));
  if (!result.success)
    throw new Error(
      "Yedek biçimi geçersiz: " +
        result.error.issues
          .slice(0, 3)
          .map((x) => x.path.join(".") + ": " + x.message)
          .join("; "),
    );
  const b = result.data;
  for (const name of [
    "words",
    "examples",
    "cards",
    "states",
    "events",
    "sessions",
    "decks",
  ] as const) {
    if (new Set(b[name].map((x) => x.id)).size !== b[name].length)
      throw new Error("Yedekte yinelenen kimlik: " + name);
  }
  const words = new Map(b.words.map((w) => [w.id, w])),
    examples = new Map(b.examples.map((e) => [e.id, e])),
    cards = new Map(b.cards.map((c) => [c.id, c])),
    sessions = new Set(b.sessions.map((s) => s.id)),
    events = new Map(b.events.map((e) => [e.id, e])),
    deckIds = new Set(b.decks.map((d) => d.id));
  const keys = new Set<string>(),
    exampleWords = new Set(b.examples.map((e) => e.wordId)),
    stateCards = new Set(b.states.map((st) => st.cardId));
  for (const w of b.words) {
    if (w.deckIds.some((d) => !deckIds.has(d)))
      throw new Error("Kelimenin koleksiyonu bulunamadı.");
    if (
      w.exampleIds?.some((k) => examples.get(k)?.wordId !== w.id) ||
      w.exampleIds?.length === 0
    )
      throw new Error("Güncel cümle ilişkisi geçersiz.");
    if (!w.deleted) {
      const k = wordKey(w);
      if (keys.has(k)) throw new Error("Yedekte yinelenen kelime anlamı.");
      keys.add(k);
      if (!exampleWords.has(w.id)) throw new Error("Kelimenin örnek cümlesi eksik.");
    }
  }
  for (const e of b.examples)
    if (!words.has(e.wordId) || !validExample(e))
      throw new Error("Cümle ilişkisi veya hedef aralığı geçersiz.");
  const cardKeys = new Set<string>();
  for (const c of b.cards) {
    const k = c.wordId + "|" + c.direction;
    if (!words.has(c.wordId) || cardKeys.has(k))
      throw new Error("Kart ilişkisi geçersiz veya yinelenmiş.");
    cardKeys.add(k);
    if (!stateCards.has(c.id)) throw new Error("Kartın tekrar durumu eksik.");
  }
  for (const w of b.words)
    if (
      !w.deleted &&
      (!cardKeys.has(w.id + "|receptive") || !cardKeys.has(w.id + "|productive"))
    )
      throw new Error("Kelimenin temel öğrenme kartları eksik.");
  for (const session of b.sessions)
    if (
      session.queue.some((cardId) => !cards.has(cardId)) ||
      new Set(session.queue).size !== session.queue.length
    )
      throw new Error("Oturum kuyruğunda eksik veya yinelenen kart var.");
  const stateKeys = new Set<string>();
  for (const st of b.states) {
    if (
      !cards.has(st.cardId) ||
      st.id !== st.cardId ||
      st.memory.cardId !== st.cardId ||
      stateKeys.has(st.cardId)
    )
      throw new Error("Tekrar durumu geçersiz.");
    stateKeys.add(st.cardId);
  }
  for (const e of b.events) {
    const c = cards.get(e.cardId),
      ex = examples.get(e.exampleId);
    if (
      !c ||
      c.wordId !== e.wordId ||
      c.direction !== e.direction ||
      !ex ||
      ex.wordId !== e.wordId ||
      !sessions.has(e.sessionId) ||
      e.before.cardId !== e.cardId ||
      e.after.cardId !== e.cardId ||
      e.before.id !== e.cardId ||
      e.after.id !== e.cardId ||
      e.before.memory.cardId !== e.cardId ||
      e.after.memory.cardId !== e.cardId ||
      e.previousDue !== e.before.memory.dueAt ||
      e.nextDue !== e.after.memory.dueAt ||
      e.after.version <= e.before.version ||
      (e.assessment === "automatic" && e.correct === null) ||
      (e.assessment === "self" && e.correct !== null)
    )
      throw new Error("Tekrar geçmişinde eksik veya çelişen ilişki.");
    if (
      e.kind === "undo" &&
      (!e.undoOf ||
        events.get(e.undoOf)?.kind !== "review" ||
        events.get(e.undoOf)?.cardId !== e.cardId)
    )
      throw new Error("Geri alma kaydı geçersiz.");
  }
  return b;
}
export async function makeBackup(d = db): Promise<Backup> {
  const s = await snapshot(d);
  return validateBackup({
    app: "sentence",
    schemaVersion: 2,
    exportedAt: new Date().toISOString(),
    ...s,
  });
}
export async function restoreBackup(raw: unknown, d = db) {
  const b = validateBackup(raw);
  await d.transaction("rw", tables(d), async () => {
    for (const t of tables(d)) await t.clear();
    await d.words.bulkAdd(b.words);
    await d.examples.bulkAdd(b.examples);
    await d.cards.bulkAdd(b.cards);
    await d.states.bulkAdd(b.states);
    await d.events.bulkAdd(b.events);
    await d.sessions.bulkAdd(b.sessions);
    await d.decks.bulkAdd(b.decks);
    await d.settings.add(b.settings);
  });
  return b;
}
export function parseCSV(raw: string): string[][] {
  if (raw.length > 20000000) throw new Error("CSV en fazla 20 MB olabilir.");
  raw = raw.replace(/^\uFEFF/, "");
  let rows: string[][] = [],
    row: string[] = [],
    field = "",
    quoted = false,
    closed = false;
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (quoted) {
      if (ch === '"') {
        if (raw[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          quoted = false;
          closed = true;
        }
      } else field += ch;
    } else if (ch === '"') {
      if (field || closed) throw new Error("CSV tırnak yapısını kontrol et.");
      quoted = true;
    } else if (ch === "," || ch === "\n" || ch === "\r") {
      row.push(field);
      field = "";
      closed = false;
      if (ch !== ",") {
        if (ch === "\r" && raw[i + 1] === "\n") i++;
        if (row.some((v) => v !== "")) rows.push(row);
        row = [];
      }
    } else {
      if (closed) throw new Error("CSV kapanış tırnağından sonra ayraç olmalı.");
      field += ch;
    }
  }
  if (quoted) throw new Error("CSV içinde kapatılmamış tırnak var.");
  row.push(field);
  if (row.some((v) => v !== "")) rows.push(row);
  if (rows.length > 50001)
    throw new Error("Bir kerede en fazla 50.000 satır aktarılabilir.");
  return rows;
}
export function csvCell(value: unknown) {
  let v = String(value ?? "");
  if (/^[\s]*[=+\-@\t\r]/.test(v)) v = "'" + v;
  return '"' + v.replace(/"/g, '""') + '"';
}
export function csvWrite(rows: unknown[][]) {
  return "\uFEFF" + rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
}
const headers = [
  "lemma",
  "sense",
  "meaning",
  "pos",
  "ipa",
  "cefr",
  "kind",
  "sentence",
  "translation",
  "collocations",
  "commonError",
  "tags",
  "decks",
  "notes",
];
export function vocabularyCSV(s: Snapshot) {
  return csvWrite([
    headers,
    ...s.words
      .filter((w) => !w.deleted)
      .flatMap((w) =>
        examplesFor(s, w.id).map((ex) => [
          w.lemma,
          w.sense,
          w.meaning,
          w.pos,
          w.ipa,
          w.cefr,
          w.kind,
          markExample(ex),
          ex.tr,
          w.collocations.join(";"),
          w.commonError,
          w.tags.join(";"),
          w.deckIds.map((d) => s.decks.find((x) => x.id === d)?.name ?? d).join(";"),
          w.notes,
        ]),
      ),
  ]);
}
export function reviewsCSV(s: Snapshot) {
  const h = [
    "id",
    "kind",
    "undoOf",
    "word",
    "sense",
    "cardId",
    "atUTC",
    "timezone",
    "localDay",
    "mode",
    "direction",
    "answer",
    "assessment",
    "correct",
    "prediction",
    "rating",
    "effectiveRating",
    "answerMs",
    "activeMs",
    "hint",
    "translation",
    "previousDue",
    "nextDue",
    "sessionId",
    "difficult",
    "beforeFSRS",
    "afterFSRS",
    "prompt",
    "expected",
    "meaningAtReview",
  ];
  return csvWrite([
    h,
    ...s.events.map((e) => [
      e.id,
      e.kind,
      e.undoOf,
      e.wordAtReview ?? s.words.find((w) => w.id === e.wordId)?.lemma,
      s.words.find((w) => w.id === e.wordId)?.sense,
      e.cardId,
      e.at,
      e.timezone,
      e.localDay,
      e.mode,
      e.direction,
      e.answer,
      e.assessment,
      e.correct,
      e.prediction,
      e.rating,
      e.effectiveRating,
      e.answerMs,
      e.activeMs,
      e.hint,
      e.translation,
      e.previousDue,
      e.nextDue,
      e.sessionId,
      e.difficult,
      JSON.stringify(e.before),
      JSON.stringify(e.after),
      e.prompt,
      e.expected,
      e.meaningAtReview,
    ]),
  ]);
}
export interface ImportRow {
  word: WordSense;
  example: ExampleSentence;
}
export function readVocabularyCSV(raw: string): ImportRow[] {
  const rows = parseCSV(raw);
  if (rows.length < 2) throw new Error("CSV başlık ve en az bir kelime içermeli.");
  const head = rows.shift()!;
  if (new Set(head).size !== head.length)
    throw new Error("CSV başlıkları yinelenemez.");
  for (const h of ["lemma", "meaning", "sentence", "translation"])
    if (!head.includes(h)) throw new Error("CSV başlığı eksik: " + h);
  const now = new Date().toISOString();
  return rows.map((r, index) => {
    try {
      if (r.length !== head.length)
        throw new Error("Sütun sayısı başlıkla eşleşmiyor.");
      const data = Object.fromEntries(
        head.map((h, i) => [h, r[i].replace(/^'(?=[\s]*[=+\-@\t\r])/, "")]),
      );
      const wordId = id();
      const split = (s: string | undefined) =>
        (s ?? "")
          .split(";")
          .map((v) => v.trim())
          .filter(Boolean);
      const word = wordSchema.parse({
        id: wordId,
        lemma: data.lemma.trim(),
        sense: data.sense?.trim() || data.meaning.trim(),
        meaning: data.meaning.trim(),
        pos: data.pos ?? "",
        ipa: data.ipa ?? "",
        cefr: data.cefr || "A1",
        kind: data.kind || "word",
        collocations: split(data.collocations),
        commonError: data.commonError ?? "",
        tags: split(data.tags),
        deckIds: split(data.decks).length ? split(data.decks) : ["Kendi eklediklerim"],
        notes: data.notes ?? "",
        favorite: false,
        difficult: false,
        active: true,
        deleted: false,
        source: "import",
        createdAt: now,
      });
      const example = exampleSchema.parse({
        id: id(),
        wordId,
        ...parseMarked(data.sentence),
        tr: data.translation,
        createdAt: now,
      });
      return { word, example };
    } catch (e) {
      throw new Error(
        `CSV satır ${index + 2}: ${e instanceof Error ? e.message : "Geçersiz kayıt"}`,
      );
    }
  });
}
export function readVocabularyJSON(raw: unknown): ImportRow[] {
  const field = z.string().max(10000),
    row = z.object({
      lemma: field.min(1),
      meaning: field.min(1),
      sentence: field.min(1),
      translation: field.min(1),
      sense: field.optional(),
      pos: field.optional(),
      ipa: field.optional(),
      cefr: z.enum(["A1", "A2", "B1", "B2"]).optional(),
      kind: z.enum(["word", "phrase"]).optional(),
      collocations: z.array(field).optional(),
      commonError: field.optional(),
      tags: z.array(field).optional(),
      decks: z.array(field).optional(),
      notes: field.optional(),
    });
  const input = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object"
      ? (raw as { words?: unknown }).words
      : undefined;
  const parsed = z.array(row).min(1).max(50000).safeParse(input);
  if (!parsed.success)
    throw new Error(
      "JSON kelime listesi geçersiz. Her öğede lemma, meaning, sentence ve translation olmalı. Cümlede hedefi [köşeli parantez] içinde belirt.",
    );
  return readVocabularyCSV(
    csvWrite([
      headers,
      ...parsed.data.map((r) =>
        headers.map((h) => {
          const v = r[h as keyof typeof r];
          return Array.isArray(v) ? v.join(";") : (v ?? "");
        }),
      ),
    ]),
  );
}
export async function importVocabulary(rows: ImportRow[], d: SentenceDB = db) {
  // Validate every row before starting writes. Duplicate senses retain existing user data.
  for (const r of rows) {
    wordSchema.parse(r.word);
    exampleSchema.parse(r.example);
    if (!validExample(r.example) || r.example.wordId !== r.word.id)
      throw new Error("İçe aktarılacak hedef aralığı veya kelime ilişkisi geçersiz.");
  }
  const prepared = await Promise.all(
    rows.map(async (r) => ({
      ...r,
      cards: await Promise.all(
        [
          "receptive",
          "productive",
          ...(r.word.collocations.length ? ["collocation"] : []),
        ].map(async (direction) => {
          const card = {
            id: r.word.id + ":" + direction,
            wordId: r.word.id,
            direction: direction as "receptive" | "productive" | "collocation",
            createdAt: r.word.createdAt,
          };
          return { card, state: await newState(card.id) };
        }),
      ),
    })),
  );
  let added = 0,
    duplicates = 0,
    examplesAdded = 0;
  await d.transaction(
    "rw",
    d.words,
    d.examples,
    d.cards,
    d.states,
    d.decks,
    async () => {
      const existing = new Map(
        (await d.words.toArray()).filter((w) => !w.deleted).map((w) => [wordKey(w), w]),
      );
      const decks = await d.decks.toArray();
      const exampleKeys = new Set(
        (await d.examples.toArray()).map(
          (e) => e.wordId + "|" + e.en + "|" + e.tr + "|" + JSON.stringify(e.spans),
        ),
      );
      for (const r of prepared) {
        const k = wordKey(r.word),
          previous = existing.get(k);
        let wordId = previous?.id ?? r.word.id;
        if (previous) duplicates++;
        else {
          const deckIds: string[] = [];
          for (const name of r.word.deckIds) {
            let deck = decks.find((d) => d.id === name || d.name === name);
            if (!deck) {
              deck = { id: id(), name, description: "" };
              decks.push(deck);
              await d.decks.add(deck);
            }
            deckIds.push(deck.id);
          }
          const word = { ...r.word, deckIds };
          await d.words.add(word);
          existing.set(k, word);
          added++;
          for (const c of r.cards) {
            await d.cards.add(c.card);
            await d.states.add(c.state);
          }
        }
        const ek =
          wordId +
          "|" +
          r.example.en +
          "|" +
          r.example.tr +
          "|" +
          JSON.stringify(r.example.spans);
        if (!exampleKeys.has(ek)) {
          await d.examples.add({ ...r.example, wordId });
          if (previous?.exampleIds) {
            const updated = {
              ...previous,
              exampleIds: [...previous.exampleIds, r.example.id],
            };
            await d.words.put(updated);
            existing.set(k, updated);
          }
          exampleKeys.add(ek);
          examplesAdded++;
        }
      }
    },
  );
  return { added, duplicates, examplesAdded };
}
export async function readFile(file: File) {
  if (file.size > 100000000) throw new Error("Dosya en fazla 100 MB olabilir.");
  return file.text();
}
export function download(name: string, data: string, type = "application/json") {
  const blob = new Blob([data], { type: type + ";charset=utf-8" }),
    url = URL.createObjectURL(blob),
    a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}
